import { useLazyQuery } from '@apollo/client';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  GetRiskAssessmentResultsByRiskIdAndControlTypeDocument,
  Risk_Assessment_Result_Control_Type_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ContentTable } from 'pdfmake/interfaces';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';
import { UNRATED } from 'src/pages/controls/lookupData';

import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';

const useAssessmentsExportTable = (
  riskId: string,
  controlType: Risk_Assessment_Result_Control_Type_Enum,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => Promise<ContentTable>, boolean] => {
  const { getLabel: getStatusLabel } = useRating('assessment_status');
  const { getLabel: getLikelihoodLabel } = useRating('likelihood');
  const { getLabel: getImpactLabel } = useRating('impact');
  const { getLabel: getRatingLabel } = useRating(
    controlType === Risk_Assessment_Result_Control_Type_Enum.Controlled
      ? 'risk_controlled'
      : 'risk_uncontrolled'
  );
  const [getAssessments, getAssessmentsResult] = useLazyQuery(
    GetRiskAssessmentResultsByRiskIdAndControlTypeDocument,
    {
      variables: {
        RiskId: riskId,
        ControlType: controlType,
      },
    }
  );
  const formId =
    controlType === 'Controlled'
      ? 'controlled_risk_assessment_result'
      : 'uncontrolled_risk_assessment_result';

  const createExportTable = async () => {
    const { data: assessmentsData } = await getAssessments();
    const assessmentsTableData = (
      assessmentsData?.risk_assessment_result ?? []
    ).map((au) => [
      au.parents?.[0]?.assessment?.Title ?? '-',
      getStatusLabel(au.parents?.[0]?.assessment?.Status) ?? '-',
      getRatingLabel(au.Rating ?? UNRATED.value),
      getImpactLabel(au.Impact ?? UNRATED.value),
      getLikelihoodLabel(au.Likelihood ?? UNRATED.value),
      toLocalDate(au.parents?.[0]?.assessment?.StartDate) ?? '-',
      toLocalDate(au.parents?.[0]?.assessment?.ActualCompletionDate) ?? '-',
      au.parents?.[0]?.assessment?.completedByUser?.FriendlyName ?? '-',
    ]);

    return createTable({
      widths: '*',
      body: [
        tableHeaders([
          getStandardFieldLabel('assessment', 'Title'),
          getStandardFieldLabel('assessment', 'Status'),
          getStandardFieldLabel(formId, 'Rating'),
          getStandardFieldLabel(formId, 'Impact'),
          getStandardFieldLabel(formId, 'Likelihood'),
          getStandardFieldLabel('assessment', 'StartDate'),
          getStandardFieldLabel('assessment', 'ActualCompletionDate'),
          getStandardFieldLabel('assessment', 'CompletedByUser'),
        ]),
        ...assessmentsTableData,
      ],
    });
  };

  return [createExportTable, getAssessmentsResult.loading];
};

export default useAssessmentsExportTable;
